// src/utils/getSubdomain.js
// ✅ GAP-SEC-008 FIX (Mar 21, 2026): Hardened multi-tenant subdomain resolution
//
// HOSTNAME MATRIX — what each environment produces:
//
//   host                  → returns
//   ──────────────────────────────────────────────
//   localhost             → "firm1" (dev fallback / env var / ?firm=)
//   127.0.0.1             → "firm1" (bare IP, same as localhost)
//   firm3.localhost       → "firm3" ← THE BUG THAT WAS HERE
//   firm1.lexcorelawtech.in → "firm1" (production — current firms)
//   kalluri.handyy.in     → "kalluri" (production — legacy-pinned firm, see
//                            LEGACY_DOMAIN_FIRMS in sendClientMagicLink.js)
//   lexcorelawtech.in     → null in prod, "firm1" in dev
//
// PREVIOUS BUG:
//   Old version checked `host.startsWith("127.")` which caught the resolved IP
//   of firm3.localhost — but window.location.hostname returns the hostname
//   string, not the resolved IP. The real issue was the production branch:
//     `parts.length >= 3` — "firm3.localhost" only has 2 parts, so it fell
//     through to the "firm1" fallback.
//
// FIX: Explicit `.localhost` suffix check before the production branch.

export function getSubdomain() {
  try {
    if (typeof window === "undefined" || !window.location) {
      console.warn("getSubdomain: window.location not available");
      return null;
    }

    const host = window.location.hostname;

    if (!host || typeof host !== "string") {
      console.warn("getSubdomain: Invalid hostname");
      return null;
    }

    console.log("getSubdomain: hostname =", host);

    const urlParam = new URLSearchParams(window.location.search).get("firm");

    // ── 1. Bare localhost / raw IP ─────────────────────────────────────────────
    // No subdomain exists — use ?firm= query param, env var, or dev default.
    // "localhost"  → no dots, no subdomain
    // "127.0.0.1"  → raw IP, no subdomain
    if (
      host === "localhost" ||
      host === "127.0.0.1" ||
      host === "0.0.0.0" ||
      /^\d+\.\d+\.\d+\.\d+$/.test(host) || // any raw IPv4
      !host.includes(".")                    // single-label host (e.g. "myhost")
    ) {
      if (urlParam) return urlParam;
      console.log("getSubdomain: bare localhost/IP — using dev fallback");
      return process.env.REACT_APP_FIRM_ID || "firm1";
    }

    // ── 2. *.localhost — hosts-file dev multi-tenant ──────────────────────────
    // "firm3.localhost" → ["firm3", "localhost"] → parts[0] = "firm3"
    // This is the LOCAL MULTI-FIRM testing pattern from the onboarding guide.
    // Must be checked BEFORE the production branch (which requires >= 3 parts).
    if (host.endsWith(".localhost")) {
      const sub = host.split(".")[0];
      if (/^[a-z0-9-]{2,40}$/.test(sub)) {
        console.log("getSubdomain: *.localhost resolved →", sub);
        return sub;
      }
      // Malformed subdomain like "-.localhost"
      console.warn("getSubdomain: malformed *.localhost subdomain:", sub);
      return urlParam || process.env.REACT_APP_FIRM_ID || "firm1";
    }

    // ── 3. Production domain: firm1.lexcorelawtech.in (or a legacy-pinned firm on handyy.in) ──
    // parts = ["firm1", "lexcorelawtech", "in"] → length 3+ → parts[0] = "firm1"
    // This parsing is domain-agnostic — it works the same for any 2-label
    // root domain, so both lexcorelawtech.in and handyy.in resolve correctly
    // without any code change here.
    const parts = host.split(".").filter((p) => p.length > 0);
    console.log("getSubdomain: domain parts =", parts);

    if (parts.length >= 3) {
      const sub = parts[0];
      if (/^[a-z0-9-]{2,40}$/.test(sub)) {
        console.log("getSubdomain: production subdomain →", sub);
        return sub;
      }
      console.warn("getSubdomain: invalid subdomain format:", sub);
    }

    // ── 4. Two-part domain in production (no subdomain) ──────────────────────
    // e.g. "lexcorelawtech.in" or "handyy.in" — the root domain, not a firm URL.
    // In production this is a config error. In dev, fall back gracefully.
    if (process.env.NODE_ENV === "production") {
      console.error(
        "GAP-SEC-008: No firm subdomain found in production URL. Host:",
        host
      );
      return null; // Callers must treat null as "block login"
    }

    // Dev fallback — shouldn't normally reach here outside of bare *.localhost
    return urlParam || process.env.REACT_APP_FIRM_ID || "firm1";
  } catch (error) {
    console.error("getSubdomain: Unexpected error", error);
    return process.env.NODE_ENV === "production" ? null : "firm1";
  }
}